import React, { useState, useEffect, useRef } from 'react';

const ValueProp = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [hasSeenTransition, setHasSeenTransition] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const sectionHeight = rect.height;
      const windowHeight = window.innerHeight;
      
      // Reset if we scroll back to the very top of the page
      if (window.scrollY < 100) {
        if (hasSeenTransition) {
          setHasSeenTransition(false);
        }
        setScrollProgress(0);
        return;
      }
      
      // If we've seen the transition, stay locked at 1
      if (hasSeenTransition) {
        setScrollProgress(1);
        return;
      }
      
      // Calculate progress normally
      const visibleTop = Math.max(0, windowHeight - rect.top);
      const visibleBottom = Math.max(0, rect.bottom);
      const visibleHeight = Math.min(visibleTop, visibleBottom, sectionHeight);
      const progress = Math.max(0, Math.min(1, visibleHeight / (sectionHeight * 0.8)));
      
      setScrollProgress(progress);
      
      // Mark transition as seen when we reach 95%+ progress
      if (progress >= 0.95) {
        setHasSeenTransition(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasSeenTransition]);

  // Chaos calendar events - overlapping and messy
  const chaosEvents = [
    { title: "Team Meeting", style: { top: '10%', left: '5%', width: '25%', height: '15%', bg: 'bg-red-400' } },
    { title: "Project Review", style: { top: '15%', left: '20%', width: '30%', height: '12%', bg: 'bg-blue-400' } },
    { title: "Client Call", style: { top: '8%', left: '35%', width: '20%', height: '18%', bg: 'bg-green-400' } },
    { title: "Budget Planning", style: { top: '25%', left: '10%', width: '35%', height: '10%', bg: 'bg-yellow-400' } },
    { title: "Performance Review", style: { top: '30%', left: '30%', width: '25%', height: '15%', bg: 'bg-purple-400' } },
    { title: "Training Session", style: { top: '20%', left: '50%', width: '22%', height: '20%', bg: 'bg-pink-400' } },
    { title: "All Hands", style: { top: '45%', left: '5%', width: '28%', height: '12%', bg: 'bg-indigo-400' } },
    { title: "Sprint Planning", style: { top: '40%', left: '25%', width: '30%', height: '18%', bg: 'bg-orange-400' } },
    { title: "Stakeholder Update", style: { top: '50%', left: '45%', width: '25%', height: '15%', bg: 'bg-teal-400' } },
    { title: "Code Review", style: { top: '65%', left: '10%', width: '20%', height: '12%', bg: 'bg-cyan-400' } },
    { title: "Design Sync", style: { top: '60%', left: '35%', width: '30%', height: '16%', bg: 'bg-lime-400' } },
    { title: "Marketing Meeting", style: { top: '70%', left: '50%', width: '25%', height: '14%', bg: 'bg-rose-400' } },
  ];

  // Focus timeline events - clean and organized
  const focusEvents = [
    { title: "Board Meeting", date: "Mar 15", color: "bg-indigo-500" },
    { title: "Company Off-site", date: "Apr 22", color: "bg-emerald-500" },
    { title: "TechCrunch Conference", date: "May 18", color: "bg-amber-500" },
  ];

  return (
    <section ref={sectionRef} className="min-h-[120vh] py-32">
      <div className="max-w-6xl mx-auto px-6">
        {/* Animated heading */}
        <div className="text-center mb-16 relative">
          <h2 className="text-5xl md:text-7xl font-bold text-gray-900 leading-tight">
            <span 
              className="inline-block transition-all duration-1000 ease-out"
              style={{
                opacity: scrollProgress < 0.7 ? 1 : 1 - (scrollProgress - 0.7) * 3.33,
                transform: `translateX(${scrollProgress < 0.7 ? 0 : -(scrollProgress - 0.7) * 300}px)`
              }}
            >
              From absolute{' '}
              <span className="bg-gradient-to-r from-pink-500 to-teal-400 bg-clip-text text-transparent">
                chaos
              </span>
            </span>
            
            <span 
              className="inline-block transition-all duration-1000 ease-out"
              style={{
                opacity: scrollProgress > 0.6 ? (scrollProgress - 0.6) * 2.5 : 0,
                transform: `translateX(${scrollProgress > 0.6 ? 0 : 300}px)`
              }}
            >
              to absolute{' '}
              <span className="bg-gradient-to-r from-pink-500 to-teal-400 bg-clip-text text-transparent">
                focus
              </span>
            </span>
          </h2>
        </div>

        {/* Visual transformation */}
        <div className="relative w-full h-96 mx-auto bg-white rounded-2xl shadow-2xl border overflow-hidden">
          
          {/* Chaos View - Traditional Calendar Grid */}
          <div 
            className="absolute inset-0 transition-all duration-1000 ease-out"
            style={{
              opacity: scrollProgress < 0.9 ? 1 : 1 - ((scrollProgress - 0.9) * 10),
              transform: `scale(${1 - scrollProgress * 0.02})`
            }}
          >
            {/* Calendar Grid */}
            <div className="absolute inset-0 p-4">
              <div className="grid grid-cols-7 gap-1 h-full">
                {/* Calendar header */}
                <div className="col-span-7 text-center font-bold text-gray-700 mb-2 text-sm">
                  March 2025
                </div>
                
                {/* Days of week */}
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="text-xs text-gray-500 text-center font-medium p-1">
                    {day}
                  </div>
                ))}
                
                {/* Calendar cells with crazy overlapping events */}
                {Array.from({ length: 35 }, (_, i) => (
                  <div key={i} className="border border-gray-200 relative text-xs">
                    <span className="absolute top-0 left-1 text-gray-400">
                      {i > 6 ? i - 6 : ''}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* Overlapping chaos events */}
              {chaosEvents.map((event, index) => (
                <div
                  key={index}
                  className={`absolute ${event.style.bg} text-white text-xs p-1 rounded shadow-lg opacity-90 transform rotate-1`}
                  style={{
                    top: event.style.top,
                    left: event.style.left,
                    width: event.style.width,
                    height: event.style.height,
                    transform: `rotate(${Math.random() * 6 - 3}deg)`,
                    zIndex: index
                  }}
                >
                  <div className="font-medium truncate">{event.title}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Focus View - Clean Timeline */}
          <div 
            className="absolute inset-0 transition-all duration-1000 ease-out flex items-center justify-center"
            style={{
              opacity: scrollProgress,
              transform: `scale(${0.8 + scrollProgress * 0.2})`
            }}
          >
            <div className="w-full max-w-2xl px-8">
              {/* Timeline line */}
              <div className="relative">
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                
                {/* Timeline events */}
                <div className="space-y-8">
                  {focusEvents.map((event, index) => (
                    <div 
                      key={index} 
                      className="flex items-center space-x-6"
                      style={{
                        opacity: scrollProgress > 0.5 + (index * 0.05) ? 1 : 0,
                        transform: `translateY(${scrollProgress > 0.5 + (index * 0.05) ? 0 : 20}px)`,
                        transition: `all 0.6s ease-out ${index * 0.1}s`
                      }}
                    >
                      {/* Date circle */}
                      <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-200 w-16 h-16 text-center flex flex-col justify-center relative z-10">
                        <div className="text-xs text-gray-600 font-medium leading-none">
                          {event.date.split(' ')[0]}
                        </div>
                        <div className="text-lg font-bold text-gray-900 leading-none">
                          {event.date.split(' ')[1]}
                        </div>
                      </div>
                      
                      {/* Event card */}
                      <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${event.color}`}></div>
                          <h3 className="font-semibold text-gray-900">{event.title}</h3>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Supporting text */}
        <div className="text-center mt-12">
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            LinearCal transforms overwhelming calendar grids into clean, 
            intuitive timelines that actually make sense of your schedule.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ValueProp;